import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
    Bars3Icon,
    XMarkIcon,
    ArrowRightOnRectangleIcon,
    ChevronDownIcon,
    Squares2X2Icon,
    UsersIcon,
    CubeIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    ShoppingBagIcon,
    ClipboardDocumentListIcon,
    PhoneIcon,
    UserCircleIcon,
    ShoppingCartIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    MapIcon,
    CloudIcon,
    BeakerIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

import { solutionsData } from '../data/solutionsData';
import GoogleTranslate from './GoogleTranslate';


const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false); // New State
    const [adminOpen, setAdminOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    // Mobile Menu Dropdown States
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);


    const { user, logout, setShowAuthPopup } = useAuth();
    const { cartCount } = useCart();
    const location = useLocation();
    const navigate = useNavigate();

    const servicesRef = useRef(null);
    const solutionsRef = useRef(null); // New Ref
    const adminRef = useRef(null);
    const profileRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (servicesRef.current && !servicesRef.current.contains(event.target)) {
                setServicesOpen(false);
            }
            if (solutionsRef.current && !solutionsRef.current.contains(event.target)) {
                setSolutionsOpen(false); // Handle outside click
            }
            if (adminRef.current && !adminRef.current.contains(event.target)) {
                setAdminOpen(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setProfileOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const servicesLinks = [
        { name: 'Crop Protection', href: '/services/crop-protection', icon: ShieldCheckIcon },
        { name: 'Expert Consultancy', href: '/services/expert-consultancy', icon: UserGroupIcon },
        { name: 'Precision Agriculture', href: '/services/precision-agriculture', icon: MapIcon },
        { name: 'Hydroponics', href: '/services/hydroponics', icon: BeakerIcon },
        { name: 'Farm Management', href: '/services/farm-management', icon: ClipboardDocumentListIcon },
    ];

    // Header.js doesn't need changes as it derives links from solutionsData.js ids which match the routes.
    const solutionsLinks = solutionsData.map(s => ({
        name: s.title,
        href: `/solutions/${s.id}`,
        icon: s.icon
    }));

    const adminLinks = [
        { name: 'Dashboard', href: '/admin/dashboard', icon: Squares2X2Icon },
        { name: 'Products', href: '/admin/products', icon: CubeIcon },
        { name: 'Categories', href: '/admin/categories', icon: Squares2X2Icon },
        { name: 'Stocks', href: '/admin/stocks', icon: ClipboardDocumentListIcon },
        { name: 'Orders', href: '/admin/orders', icon: ShoppingBagIcon },
        { name: 'Users', href: '/admin/users', icon: UsersIcon },
        { name: 'Feedbacks', href: '/admin/feedbacks', icon: ChatBubbleLeftRightIcon },
        { name: 'Blogs', href: '/admin/blogs', icon: DocumentTextIcon },
        { name: 'Inquiries', href: '/admin/contact', icon: ChatBubbleLeftRightIcon },
    ];

    const isActive = (path) => location.pathname === path;



    return (
        <header className="font-sans sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100 transition-all duration-300">
            {/* Top Utility Bar - Green Theme */}
            <div className="bg-green-700 text-white text-[10px] py-1">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <div className="flex-1 overflow-hidden mr-4">
                        <div className="animate-marquee whitespace-nowrap flex items-center">
                            <span className="mx-4 flex items-center shadow-sm">
                                Healthy and Wealthy Agriculture with us
                            </span>
                            <span className="mx-4 flex items-center shadow-sm">
                                <PhoneIcon className="h-3 w-3 mr-1" />
                                Call: +91 7859837869
                            </span>
                        </div>
                    </div>
                    {/* Language Switcher */}
                    <div className="z-50 relative">
                        <GoogleTranslate />
                    </div>
                </div>
            </div>

            {/* Main Header - Full Width */}
            <div className="w-full">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">

                    {/* Logo - Optimized Size & Alignment */}
                    <div className="flex-shrink-0 -ml-2 transition-transform hover:scale-105 duration-300">
                        <Link to="/" className="flex items-center">
                            <img
                                className="h-14 w-auto md:h-20 object-contain drop-shadow-sm"
                                src="/logo.png"
                                alt="Marvelous Agri"
                            />
                        </Link>
                    </div>

                    {/* Desktop Navigation - Increased Spacing */}
                    <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
                        <div className="flex items-center justify-between w-full whitespace-nowrap">
                            {[
                                { name: 'Home', path: '/' },
                                { name: 'About Us', path: '/about' },
                                { name: 'Services', path: '/services', isDropdown: true },
                                // { name: 'Products', path: '/products' },
                                { name: 'Projects', path: '/projects' },
                                { name: 'Solutions', path: '/solutions', isDropdown: true },
                                { name: 'Blogs', path: '/blogs' },
                                { name: 'Our App', path: '/app' },
                                { name: 'Contact', path: '/contact' }
                            ].map((item, index) => (
                                <React.Fragment key={item.name}>


                                    {item.isDropdown ? (
                                        <div
                                            className="relative group py-4 px-1"
                                            ref={item.name === 'Services' ? servicesRef : solutionsRef}
                                            onMouseEnter={() => item.name === 'Services' ? setServicesOpen(true) : setSolutionsOpen(true)}
                                            onMouseLeave={() => item.name === 'Services' ? setServicesOpen(false) : setSolutionsOpen(false)}
                                        >
                                            <Link
                                                to={item.path}
                                                className={`text-sm font-bold uppercase tracking-wide transition-colors relative group-hover:text-green-600 ${location.pathname.startsWith(item.path) ? 'text-green-600' : 'text-slate-700'
                                                    }`}
                                            >
                                                {item.name}
                                                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-green-600 transition-all duration-300 group-hover:w-full ${location.pathname.startsWith(item.path) ? 'w-full' : ''
                                                    }`}></span>
                                            </Link>

                                            {/* Dropdown Logic */}
                                            {((item.name === 'Services' && servicesOpen) || (item.name === 'Solutions' && solutionsOpen)) && (
                                                <div className={`absolute left-1/2 transform -translate-x-1/2 top-full pt-4 z-50 ${item.name === 'Solutions' ? 'w-[750px]' : 'w-72'}`}>
                                                    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border-t-4 border-green-600 overflow-hidden ring-1 ring-black ring-opacity-5">
                                                        <div className={`grid ${item.name === 'Solutions' ? 'grid-cols-2 gap-4 p-5' : 'grid-cols-1 py-2'}`}>
                                                            {(item.name === 'Services' ? servicesLinks : solutionsLinks).map((link) => (
                                                                <Link
                                                                    key={link.name}
                                                                    to={link.href}
                                                                    className={`group flex items-center px-4 py-3 text-md font-medium text-slate-600 hover:text-green-700 hover:bg-green-50/80 rounded-xl transition-all duration-200 ${item.name !== 'Solutions' ? 'border-b border-gray-50 last:border-0' : ''}`}
                                                                    onClick={() => {
                                                                        setServicesOpen(false);
                                                                        setSolutionsOpen(false);
                                                                    }}
                                                                >
                                                                    <div className="flex-shrink-0 mr-3 h-10 w-10 flex items-center justify-center rounded-lg bg-green-100/50 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                                                                        {link.icon ? <link.icon className="h-6 w-6" aria-hidden="true" /> : <CubeIcon className="h-6 w-6" />}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <span className="block text-slate-800 font-bold group-hover:text-green-700 leading-tight">
                                                                            {link.name}
                                                                        </span>
                                                                    </div>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="py-4 px-1 relative group">
                                            <Link
                                                to={item.path}
                                                className={`text-sm font-bold uppercase tracking-wide transition-colors relative group-hover:text-green-600 ${isActive(item.path) ? 'text-green-600' : 'text-slate-700'
                                                    }`}
                                            >
                                                {item.name}
                                                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-green-600 transition-all duration-300 group-hover:w-full ${isActive(item.path) ? 'w-full' : ''
                                                    }`}></span>
                                            </Link>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Right Side Buttons */}
                    <div className="flex items-center space-x-5">

                        {/* Admin Button - Premium Outline */}
                        {user?.role === 'admin' && (
                            <div className="relative" ref={adminRef}>
                                <button
                                    onClick={() => setAdminOpen(!adminOpen)}
                                    className="hidden md:flex items-center px-5 py-2 rounded-full border border-green-600 text-green-700 text-xs font-bold tracking-wide hover:bg-green-600 hover:text-white hover:shadow-lg transition-all duration-300 uppercase active:scale-95"
                                >
                                    ADMIN
                                </button>
                                {adminOpen && (
                                    <div className="absolute right-0 mt-3 w-56 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border-t-4 border-green-600 overflow-hidden py-2 z-50 ring-1 ring-black ring-opacity-5">
                                        {adminLinks.map((link) => (
                                            <Link
                                                key={link.name}
                                                to={link.href}
                                                className="flex items-center px-4 py-3 text-sm text-slate-600 hover:bg-green-50 hover:text-green-700 transition-colors"
                                                onClick={() => setAdminOpen(false)}
                                            >
                                                {link.icon && <link.icon className="mr-3 h-4 w-4" />}
                                                {link.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Login/Profile Button - Premium Styles */}
                        {user ? (
                            <div className="relative" ref={profileRef}>
                                <button
                                    onClick={() => setProfileOpen(!profileOpen)}
                                    className="flex items-center justify-center h-10 w-10 md:h-11 md:w-11 rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 hover:border-green-300 transition-all shadow-sm hover:shadow-md active:scale-95"
                                >
                                    <span className="font-bold text-lg">{user.name?.charAt(0)}</span>
                                </button>
                                {profileOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border-t-4 border-green-600 overflow-hidden py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <p className="text-sm font-bold text-green-900">{user.name}</p>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        {/* <Link
    to="/orders"
    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
    onClick={() => setProfileOpen(false)}
>
    My Orders
</Link>
<Link
    to="/wishlist"
    className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
    onClick={() => setProfileOpen(false)}
>
    Wishlist
</Link> */}
                                        <button
                                            onClick={() => {
                                                setProfileOpen(false);
                                                logout();
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center border-t border-gray-100"
                                        >
                                            <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowAuthPopup(true)}
                                className="flex items-center px-6 py-2.5 rounded-full bg-gradient-to-r from-green-600 to-green-800 text-white text-xs font-bold tracking-wide shadow-lg shadow-green-600/20 hover:shadow-green-600/40 hover:scale-105 transition-all duration-300 transform"
                            >
                                LOGIN
                                <ArrowRightOnRectangleIcon className="ml-2 h-4 w-4" />
                            </button>
                        )}

                        {/* Mobile Menu Button */}
                        <div className="lg:hidden">
                            <button
                                type="button"
                                className="p-2 text-green-900 rounded-md hover:bg-green-50"
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                {isOpen ? (
                                    <XMarkIcon className="h-6 w-6" />
                                ) : (
                                    <Bars3Icon className="h-6 w-6" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Modal Overlay - High Z-index to cover everything */}
            {isOpen && (
                <div className="lg:hidden fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300" onClick={() => setIsOpen(false)}>

                    {/* Mobile Menu Content Card */}
                    <div
                        className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col pt-48 relative transform transition-all scale-100 opacity-100"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-green-50 shrink-0">
                            <span className="font-bold text-green-900 text-lg">Menu</span>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-2 rounded-full hover:bg-green-100 text-green-900 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                            <Link to="/" className={`block px-4 py-3 rounded-xl text-sm font-bold border-l-4 mb-1 ${isActive('/') ? 'border-green-600 bg-green-50 text-green-700' : 'border-transparent text-gray-900 hover:bg-gray-100'}`} onClick={() => setIsOpen(false)}>
                                Home
                            </Link>
                            <Link to="/about" className={`block px-4 py-3 rounded-xl text-sm font-bold border-l-4 mb-1 ${isActive('/about') ? 'border-green-600 bg-green-50 text-green-700' : 'border-transparent text-gray-900 hover:bg-gray-100'}`} onClick={() => setIsOpen(false)}>
                                About Us
                            </Link>

                            {/* Mobile Services Accordion */}
                            <div className="rounded-xl overflow-hidden">
                                <button
                                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                                    className="w-full flex justify-between items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Services
                                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? 'transform rotate-180' : ''}`} />
                                </button>
                                <div className={`transition-all duration-300 ease-in-out ${mobileServicesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className="pl-4 pr-2 pb-2 space-y-1">
                                        <Link to="/services" className="block px-4 py-2 rounded-lg text-sm font-bold text-green-700 bg-green-50 hover:bg-green-100" onClick={() => setIsOpen(false)}>View All Services</Link>
                                        {servicesLinks.map((link) => (
                                            <Link key={link.name} to={link.href} className="block px-4 py-2 rounded-lg text-sm text-gray-600 hover:text-green-600 hover:bg-gray-50" onClick={() => setIsOpen(false)}>{link.name}</Link>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <Link to="/projects" className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive('/projects') ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsOpen(false)}>Projects</Link>

                            {/* Mobile Solutions Accordion */}
                            <div className="rounded-xl overflow-hidden">
                                <button
                                    onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                                    className="w-full flex justify-between items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Solutions
                                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${mobileSolutionsOpen ? 'transform rotate-180' : ''}`} />
                                </button>
                                <div className={`transition-all duration-300 ease-in-out ${mobileSolutionsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className="pl-4 pr-2 pb-2 space-y-1">
                                        <Link to="/solutions" className="block px-4 py-2 rounded-lg text-sm font-bold text-green-700 bg-green-50 hover:bg-green-100" onClick={() => setIsOpen(false)}>View All Solutions</Link>
                                        {solutionsLinks.map((link) => (
                                            <Link key={link.name} to={link.href} className="block px-4 py-2 rounded-lg text-sm text-gray-600 hover:text-green-600 hover:bg-gray-50" onClick={() => setIsOpen(false)}>{link.name}</Link>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <Link to="/blogs" className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive('/blogs') ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsOpen(false)}>Blogs</Link>
                            <Link to="/app" className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive('/app') ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsOpen(false)}>Our App</Link>
                            <Link to="/contact" className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive('/contact') ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setIsOpen(false)}>Contact</Link>

                            {/* Mobile Login/Profile Section */}
                            <div className="mt-4 pt-4 border-t border-gray-100 px-2 pb-2">
                                {user ? (
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <div className="flex items-center mb-3">
                                            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold mr-3 border border-green-200">
                                                {user.name?.charAt(0)}
                                            </div>
                                            <div className="overflow-hidden">
                                                <p className="font-bold text-gray-900 truncate">{user.name}</p>
                                                <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setIsOpen(false);
                                                logout();
                                            }}
                                            className="w-full flex items-center justify-center px-4 py-2 bg-white border border-red-100 text-red-600 rounded-lg hover:bg-red-50 font-semibold text-sm transition-colors shadow-sm"
                                        >
                                            <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
                                            Logout
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setIsOpen(false);
                                            setShowAuthPopup(true);
                                        }}
                                        className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-bold shadow-lg shadow-green-600/20 active:scale-95 transition-all"
                                    >
                                        Login / Sign Up
                                        <ArrowRightOnRectangleIcon className="ml-2 h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Admin Links for Mobile */}
                            {user?.role === 'admin' && (
                                <div className="mt-2 pt-2 border-t border-gray-100">
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</div>
                                    {adminLinks.map((link) => (
                                        <Link
                                            key={link.name}
                                            to={link.href}
                                            className={`block px-4 py-3 rounded-xl text-sm font-medium ${isActive(link.href) ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <div className="flex items-center">
                                                {link.icon && <link.icon className="mr-3 h-5 w-5" />}
                                                {link.name}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
